import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../LanguageContext';


// ─── Color Palette ──────────────────────────────────────────
const COLORS = {
    rawMaterial: { bg: '#E3F2FD', border: '#1976D2', text: '#0D47A1', accent: '#1976D2' },
    product: { bg: '#E8F5E9', border: '#2E7D32', text: '#1B5E20', accent: '#2E7D32' },
    line: '#B0BEC5',
    lineActive: '#78909C',
    bg: '#fafbfc',
};

// ─── Icons ──────────────────────────────────────────────────
const RawIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="7" width="20" height="10" rx="2" stroke={COLORS.rawMaterial.accent} strokeWidth="1.5" />
        <path d="M6 7V5a2 2 0 012-2h8a2 2 0 012 2v2" stroke={COLORS.rawMaterial.accent} strokeWidth="1.5" />
        <line x1="12" y1="10" x2="12" y2="14" stroke={COLORS.rawMaterial.accent} strokeWidth="1.5" strokeLinecap="round" />
        <line x1="10" y1="12" x2="14" y2="12" stroke={COLORS.rawMaterial.accent} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);
const ProductIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M12 2L2 7l10 5 10-5-10-5z" stroke={COLORS.product.accent} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M2 17l10 5 10-5" stroke={COLORS.product.accent} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M2 12l10 5 10-5" stroke={COLORS.product.accent} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
);

// ─── Helpers ────────────────────────────────────────────────
const pct = (part, total) => total > 0 ? Math.round((part / total) * 100) : 0;

// ─── Node ───────────────────────────────────────────────────
const FlowNode = ({ x, y, w, h, colors, icon, title, subtitle, badge, uuid, type, currentUuid, currentLabel }) => {
    const navigate = useNavigate();
    const { workspaceUuid } = useParams();
    const isNavigable = !!uuid && uuid !== currentUuid;

    const handleNav = (e) => {
        if (!isNavigable) return;
        e.stopPropagation();
        const base = type === 'raw' ? `/workspace/${workspaceUuid}/raw-materials` : `/workspace/${workspaceUuid}/products`;
        navigate(`${base}/${uuid}?autoOpenOverview=true`);
    };

    return (
        <g style={{ cursor: isNavigable ? 'pointer' : 'default' }} onClick={handleNav}>
            <rect x={x} y={y} width={w} height={h} rx={12}
                fill={colors.bg} stroke={colors.border} strokeWidth="1.5"
                style={{ filter: 'drop-shadow(0px 2px 6px rgba(0,0,0,0.08))' }}
            />
            {isNavigable && (
                <g transform={`translate(${x + w - 24}, ${y + 8})`}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2c6ecb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                </g>
            )}
            <foreignObject x={x} y={y} width={w} height={h}>
                <div xmlns="http://www.w3.org/1999/xhtml" style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    height: '100%', width: '100%', padding: '4px 8px', boxSizing: 'border-box', textAlign: 'center',
                    userSelect: 'none'
                }}>
                    <div style={{ marginBottom: 2 }}>{icon}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: colors.text, lineHeight: 1.2, maxWidth: w - 16 }}>{title}</div>
                    {subtitle && <div style={{ fontSize: 9, color: colors.text, opacity: 0.7, marginTop: 1 }}>{subtitle}</div>}
                    {badge && (
                        <div style={{
                            marginTop: 3, fontSize: 9, fontWeight: 700, color: '#fff', background: colors.accent,
                            borderRadius: 99, padding: '1px 8px', lineHeight: '16px'
                        }}>{badge}</div>
                    )}
                </div>
            </foreignObject>
            {currentLabel && (
                <text
                    x={x + w / 2}
                    y={y + h + 15}
                    textAnchor="middle"
                    fill={colors.accent}
                    style={{ fontSize: 10, fontWeight: 700 }}
                >
                    {currentLabel}
                </text>
            )}
        </g>
    );
};



// ─── Connection ─────────────────────────────────────────────
const FlowLine = ({ x1, y1, x2, y2, label, labelColor, forceIsRtl }) => {
    const { isRtl: contextIsRtl } = useLanguage();
    const isRtl = forceIsRtl !== undefined ? forceIsRtl : contextIsRtl;
    const midY = (y1 + y2) / 2;
    const d = `M${x1},${y1} C${x1},${midY} ${x2},${midY} ${x2},${y2}`;
    const labelX = (x1 + x2) / 2;
    const labelY = midY;
    return (
        <g>
            <path d={d} fill="none" stroke={COLORS.line} strokeWidth="2" strokeDasharray="6,3" style={{ opacity: 0.6 }} />
            <path d={d} fill="none" stroke={COLORS.lineActive} strokeWidth="2" style={{ opacity: 0 }}>
                <animate attributeName="opacity" values="0;0.5;0" dur="3s" repeatCount="indefinite" />
            </path>
            <circle cx={x2} cy={y2} r="4" fill={COLORS.lineActive} style={{ opacity: 0.6 }} />
            {label && (
                <g>
                    <rect x={labelX - 22} y={labelY - 9} width={44} height={18} rx={9} fill={labelColor || '#455A64'} style={{ opacity: 0.9 }} />
                    <text x={labelX} y={labelY + 4} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="700">{typeof label === 'number' ? `${label.toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 1 })}%` : label}</text>
                </g>
            )}
        </g>
    );
};

// ─── Main Diagram ───────────────────────────────────────────
const RawMaterialFlowDiagram = ({ rawMaterial, products, currentUuid, forceT, forceIsRtl }) => {
    const { t: contextT, isRtl: contextIsRtl } = useLanguage();
    const t = forceT || contextT;
    const isRtl = forceIsRtl !== undefined ? forceIsRtl : contextIsRtl;
    const prods = products || [];
    const initialQty = Number(rawMaterial?.actual_quantity || rawMaterial?.available_quantity || 0);
    const availableQty = Number(rawMaterial?.available_quantity || 0);

    // Layout config
    const RAW_W = 180;
    const RAW_H = 80;
    const PROD_W = 140;
    const PROD_H = 72;
    const GAP_X = 24;
    const Y_RAW = 30;
    const Y_PROD = 220;

    const outItems = [
        ...prods.map(p => {
            const used = (p.used_raw_materials || []).find(rm =>
                rm.rawMaterialId?._id === rawMaterial?._id || rm.rawMaterialId === rawMaterial?._id
            );
            return {
                label: p.product_name,
                qty: Number(used?.quantity || 0),
                colors: COLORS.product,
                icon: <ProductIcon />,
                uuid: p.uuid
            };

        })
    ];

    if (availableQty > 0) {
        outItems.push({
            label: t.in_stock,
            qty: availableQty,
            colors: COLORS.rawMaterial,
            icon: <RawIcon />
        });
    }

    const outCount = outItems.length || 1;
    const bottomRowWidth = outCount * PROD_W + (outCount - 1) * GAP_X;
    const svgWidth = Math.max(bottomRowWidth, RAW_W) + 120;
    const svgHeight = Y_PROD + PROD_H + 40;
    const centerX = svgWidth / 2;

    const rawPos = { x: centerX - RAW_W / 2, y: Y_RAW };
    const outPositions = outItems.map((_, i) => ({
        x: centerX - bottomRowWidth / 2 + i * (PROD_W + GAP_X),
        y: Y_PROD,
    }));

    // Quantity used per product (approximate: equal split if no specific data)
    const usedPerProduct = prods.map(p => {
        // Try to find how much of this raw material was used in this product
        const usedRaw = (p.used_raw_materials || []).find(
            rm => rm.rawMaterialId?._id === rawMaterial?._id || rm.rawMaterialId === rawMaterial?._id
        );
        return Number(usedRaw?.quantity || 0);
    });
    const totalUsed = usedPerProduct.reduce((s, q) => s + q, 0);

    return (
        <div style={{ overflow: 'visible', padding: 20, background: COLORS.bg, borderRadius: 12, minHeight: svgHeight + 60 }}>
            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, justifyContent: 'center', direction: isRtl ? 'rtl' : 'ltr' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: COLORS.rawMaterial.accent }} />
                    <span style={{ fontSize: 11, color: '#455A64', fontWeight: 500 }}>{t.raw_material}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: COLORS.product.accent }} />
                    <span style={{ fontSize: 11, color: '#455A64', fontWeight: 500 }}>{t.product}</span>
                </div>
            </div>

            <svg width={svgWidth} height={svgHeight} style={{ display: 'block', margin: '0 auto' }}>
                {/* Connections: Raw Material → Products */}
                {outItems.map((item, i) => {
                    const pos = outPositions[i];
                    const fromX = rawPos.x + RAW_W / 2;
                    const fromY = rawPos.y + RAW_H;
                    const toX = pos.x + PROD_W / 2;
                    const toY = pos.y;
                    const percentage = initialQty > 0 ? pct(item.qty, initialQty) : 0;
                    return (
                        <FlowLine
                            key={`conn-${i}`}
                            x1={fromX} y1={fromY}
                            x2={toX} y2={toY}
                            label={item.qty > 0 ? percentage : '—'}
                            labelColor={item.colors.accent}
                            forceIsRtl={forceIsRtl}
                        />
                    );
                })}

                {/* Raw Material Node (top center) */}
                <FlowNode
                    x={rawPos.x} y={rawPos.y} w={RAW_W} h={RAW_H}
                    colors={COLORS.rawMaterial}
                    icon={<RawIcon />}
                    title={rawMaterial?.material_name || t.raw_material}
                    subtitle={`${initialQty.toLocaleString(isRtl ? 'ar-EG' : undefined)} ${t.kg} ${t.initial_label}`}
                    badge={rawMaterial?.batch_number}
                    uuid={rawMaterial?.uuid}
                    type="raw"
                    currentUuid={rawMaterial?.uuid}
                />



                {/* Product Nodes (bottom row) */}
                {outItems.length > 0 ? outItems.map((item, i) => {
                    const pos = outPositions[i];
                    return (
                        <FlowNode
                            key={`out-${i}`}
                            x={pos.x} y={pos.y} w={PROD_W} h={PROD_H}
                            colors={item.colors}
                            icon={item.icon}
                            title={item.label}
                            subtitle={`${Number(item.qty || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })} ${t.kg}`}
                            uuid={item.uuid}
                            type="product"
                            currentUuid={rawMaterial?.uuid}
                        />


                    );
                }) : (
                    <FlowNode
                        x={centerX - PROD_W / 2} y={Y_PROD} w={PROD_W} h={PROD_H}
                        colors={COLORS.product}
                        icon={<ProductIcon />}
                        title={t.no_distribution}
                        subtitle="—"
                    />
                )}
            </svg>

            {/* Summary Cards */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginTop: 24, direction: isRtl ? 'rtl' : 'ltr' }}>
                <SummaryCard label={t.initial_stock} value={`${initialQty.toLocaleString(isRtl ? 'ar-EG' : undefined)} ${t.kg}`} color={COLORS.rawMaterial.accent} />
                <SummaryCard label={t.available_stock} value={`${availableQty.toLocaleString(isRtl ? 'ar-EG' : undefined)} ${t.kg}`} color="#1976d2" />
                <SummaryCard label={t.products_made} value={`${prods.length.toLocaleString(isRtl ? 'ar-EG' : undefined)}`} color={COLORS.product.accent} />
                {outItems.filter(v => v.label !== t.in_stock).reduce((s, v) => s + v.qty, 0) > 0 &&
                    <SummaryCard label={t.total_qty_used_diag} value={`${outItems.filter(v => v.label !== t.in_stock).reduce((s, v) => s + v.qty, 0).toLocaleString(isRtl ? 'ar-EG' : undefined)} ${t.kg}`} color="#455A64" />
                }
            </div>
        </div>
    );
};

const SummaryCard = ({ label, value, color }) => (
    <div style={{
        background: '#fff', borderRadius: 10, padding: '12px 20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #ebebeb',
        minWidth: 120, textAlign: 'center'
    }}>
        <div style={{ fontSize: 11, color: '#6d7175', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
    </div>
);

export default RawMaterialFlowDiagram;
